// Vendor side (demo "Check Invoice" scene): Is this your invoice? -> yes/no ->
// (verify mode) TrustStamp through the Task 3 recipient-request endpoints with
// request_token -> payout details form (TemplateForm, unchanged) -> result.
import { useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { PhoneInput } from '@/components/form/fields/PhoneInput/PhoneInput';
import { DATE_TIME_FORMAT, formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { prepareResponse, getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { ID_VERIFICATION_POLLING_INTERVAL, isIdVerificationLiveStatus } from '@/helpers/verificationHelpers';
import {
  answerInvoiceCheckApi,
  openInvoiceCheckApi,
  submitInvoiceCheckApi,
} from '@/api/v1/invoice-checks/invoiceCheckPublicApi';
import { startRecipientRequestVerificationApi } from '@/api/v1/recipient-requests/startRecipientRequestVerificationApi';
import { getRecipientRequestVerificationStatusApi } from '@/api/v1/recipient-requests/getRecipientRequestVerificationStatusApi';
import { resendRecipientRequestVerificationApi } from '@/api/v1/recipient-requests/resendRecipientRequestVerificationApi';
import { SubmitRecipientProfilePayloadType } from '@/api/v1/recipient-requests/submitRecipientProfileApi';
import { SecureLinkCard, SecureLinkShell } from '@/bundle/pages/SecureLinkPage/styles';
import { TemplateForm } from '@/bundle/pages/RecipientRequestPage/ui/TemplateForm';
import { RecipientRequestVerificationInProgress } from '@/bundle/pages/RecipientRequestPage/ui/RecipientRequestVerificationInProgress';
import { showSnackbarResponseError } from '@/helpers/helpers';

const DEAD_MESSAGE: Record<string, string> = {
  expired: 'This request has expired. Ask the payer for a new one.',
  revoked: 'This request was withdrawn by the payer.',
  declined: 'You told us this invoice is not yours. The payer has been alerted; nothing else is needed from you.',
};

export const InvoiceCheckPage = () => {
  const { token } = useParams();
  const [phone, setPhone] = useState('');
  const [showPhoneForm, setShowPhoneForm] = useState(false);
  const [result, setResult] = useState<{ result: 'match' | 'mismatch'; organization: string } | null>(null);

  const {
    data: openResponse,
    isPending,
    refetch,
  } = useQuery({
    queryKey: ['invoice_check_open', token],
    queryFn: async () => prepareResponse(await openInvoiceCheckApi(token)),
    retry: false,
  });
  const state = openResponse?.body;
  const requestToken = state?.request_token;

  const { data: statusResponse, refetch: refetchStatus } = useQuery({
    queryKey: ['recipient_request_verify_status', requestToken],
    queryFn: async () => prepareResponse(await getRecipientRequestVerificationStatusApi(requestToken)),
    enabled: !!requestToken && !!state?.requires_verification && state?.answer === 'yes',
    refetchInterval: (query) =>
      isIdVerificationLiveStatus(query.state.data?.body?.status) ? ID_VERIFICATION_POLLING_INTERVAL : false,
    refetchIntervalInBackground: true,
  });
  const verifyStatus = statusResponse?.body;

  const answer = useMutation({
    mutationFn: async (value: 'yes' | 'no') => prepareResponse(await answerInvoiceCheckApi(token, value)),
    onSuccess(response) {
      if (response.error) return showSnackbarResponseError(response.error);

      refetch();
    },
  });
  const start = useMutation({
    mutationFn: async () => prepareResponse(await startRecipientRequestVerificationApi(requestToken, phone)),
    onSuccess(response) {
      if (response.error) return showSnackbarResponseError(response.error);

      setShowPhoneForm(false);
      refetchStatus();
    },
  });
  const resend = useMutation({
    mutationKey: ['recipient_request_verify_resend', requestToken],
    mutationFn: async () => prepareResponse(await resendRecipientRequestVerificationApi(requestToken)),
    onSuccess(response) {
      if (response.error) return showSnackbarResponseError(response.error);

      refetchStatus();
    },
  });
  const submit = useMutation({
    mutationFn: async (payload: SubmitRecipientProfilePayloadType) =>
      prepareResponse(await submitInvoiceCheckApi(token, payload)),
    onSuccess(response) {
      if (response.error) {
        return showSnackbarResponseError(response.error);
      }

      setResult({ result: response.body.result, organization: response.body.organization });
    },
  });

  if (isPending) {
    return (
      <SecureLinkShell>
        <SecureLinkCard>
          <Typography>Loading...</Typography>
        </SecureLinkCard>
      </SecureLinkShell>
    );
  }

  if (!state || openResponse?.error) {
    return (
      <SecureLinkShell>
        <SecureLinkCard>
          <Typography variant='headline5'>This request cannot be opened.</Typography>
          <Typography variant='bodySmall'>{getResponseError(openResponse?.error) || 'The link is invalid.'}</Typography>
        </SecureLinkCard>
      </SecureLinkShell>
    );
  }

  const dead = DEAD_MESSAGE[state.state];
  const verified = !state.requires_verification || verifyStatus?.status === 'succeed';
  const inProgress = isIdVerificationLiveStatus(verifyStatus?.status);

  return (
    <SecureLinkShell>
      <SecureLinkCard $maxWidth='800px'>
        <Typography variant='headline4' fontWeight='medium'>
          WireVault invoice check
        </Typography>

        {result ? (
          result.result === 'match' ? (
            <>
              <Typography variant='subTitle'>Details confirmed</Typography>
              <Typography>
                Your payout details match invoice {state.invoice_number}. <b>{result.organization}</b> can now pay it.
                You will be told when the payment goes out.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant='subTitle'>Details do not match the invoice</Typography>
              <Typography>
                The account you entered is not the one printed on invoice {state.invoice_number}.{' '}
                <b>{result.organization}</b> has been alerted and will contact you directly before paying. If you did
                not send this invoice, tell them now.
              </Typography>
            </>
          )
        ) : dead ? (
          <>
            <Typography variant='subTitle'>Request closed</Typography>
            <Typography>{dead}</Typography>
          </>
        ) : state.answer !== 'yes' ? (
          <>
            <Typography variant='subTitle'>Is this your invoice?</Typography>
            <Typography>
              <b>{state.payer}</b> received an invoice that appears to be from <b>{state.vendor_name}</b>: number{' '}
              <b>{state.invoice_number}</b>, {state.amount_label}. Before paying, they ask you to confirm it is yours
              and to enter your payout details yourself. The account on the invoice is never shown to you.
            </Typography>
            <Box display='flex' gap='12px'>
              <ButtonNew variant='danger' isLoading={answer.isPending} onClick={() => answer.mutate('no')}>
                No, I did not send this
              </ButtonNew>
              <ButtonNew isLoading={answer.isPending} onClick={() => answer.mutate('yes')}>
                Yes, this is my invoice
              </ButtonNew>
            </Box>
          </>
        ) : !verified ? (
          <>
            <Typography variant='subTitle'>Verify your identity</Typography>
            <Typography>
              {state.payer} needs to know who is giving them bank details. A government ID and a short liveness check
              through TrustStamp, about two minutes.
            </Typography>
            {(!inProgress || showPhoneForm) && (
              <Box display='flex' flexDirection='column' rowGap='12px'>
                <PhoneInput name='mobile_phone' label='Your mobile number' value={phone} onChange={setPhone} />
                <ButtonNew size='medium' isLoading={start.isPending} disabled={!phone} onClick={() => start.mutate()}>
                  Start verification
                </ButtonNew>
              </Box>
            )}
            {inProgress && (
              <RecipientRequestVerificationInProgress
                key={verifyStatus.session_id ?? 'session'}
                phone={verifyStatus.phone_masked}
                isLoading={resend.isPending}
                canResend={verifyStatus.can_resend}
                onResend={() => resend.mutate()}
                onChangeNumber={() => setShowPhoneForm(true)}
                resendIn={verifyStatus.start_new_session_enable_after_sec ?? 0}
                onTimerEnds={refetchStatus}
              />
            )}
          </>
        ) : (
          <>
            <Typography variant='subTitle'>Your payout details for invoice {state.invoice_number}</Typography>
            <Typography>
              Enter the account you want {state.payer} to pay, in the order their bank ({state.template_label}) asks for
              it. We compare it with the invoice and tell you both the result right away.
            </Typography>
            <TemplateForm
              fields={state.fields ?? []}
              filledBy='recipient'
              wireConfirmation={!!state.wire_confirmation}
              isLoading={submit.isPending}
              apiError={submit.data?.error}
              onSubmit={(payload) => submit.mutate(payload)}
            />
          </>
        )}
        <Typography variant='caption' fontWeight='medium' textAlign='right'>
          Expires {formatDate(state.expires_at, DATE_TIME_FORMAT)}
        </Typography>
      </SecureLinkCard>
    </SecureLinkShell>
  );
};
